<template>
  <section class="well should-you-bluff-initial-conclusion">
    <section>
      <div class="page-header">
        <h1 v-if="streetToBluff !== 'river'">Initial analysis</h1>
        <h1 v-if="streetToBluff === 'river'">Analysis</h1>
      </div>
      <table class="table">
        <thead>
          <tr>
            <th>Fold equity</th>
            <th>Break even percentage for {{ bluffSize }} pot bluff</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ foldEquity }}%</td>
            <td>{{ immediatelyProfitableBreakevenPercentage }}%</td>
          </tr>
        </tbody>
      </table>
      <section class="alert" v-bind:class="{ 'alert-success': isBluffImmediatelyProfitable, 'alert-danger': !isBluffImmediatelyProfitable }">
        <p>Your {{ bluffSize }} pot bluff <em>will <span v-if="!isBluffImmediatelyProfitable">not</span></em> be <span v-if="streetToBluff !== 'river'">immediately </span>profitable <span v-if="isBluffImmediatelyProfitable">✅</span><span v-else>❌</span></p>
      </section>
      Reasoning:
      <ul v-if="numPlayers === 1">
        <li>
          A {{ bluffSize }} pot bluff needs to work more than {{ immediatelyProfitableBreakevenPercentage }}% of the time in order to be <span v-if="streetToBluff !== 'river'">immediately </span>profitable.
          <toggle-help-button
            v-bind:helpAreaVisible="helpAreaVisible.reasoning[0]"
            v-on:toggleHelp="toggleHelp('reasoning', 0)"
          />
          <reasoning-help-section-1 v-if="helpAreaVisible.reasoning[0]"/>
        </li>
        <li>
          According to your assumptions, your opponent will be continuing {{ hitPercentageForStreetToBluff }}% of the time versus your {{ bluffSize }} pot {{ streetToBluff }} bluff.
          <toggle-help-button
            v-bind:helpAreaVisible="helpAreaVisible.reasoning[1]"
            v-on:toggleHelp="toggleHelp('reasoning', 1)"
          />
          <div class="well help-section" v-if="helpAreaVisible.reasoning[1]">
            This comes from the ranges you set. How many combos did your opponent have entering the {{ streetToBluff }}? How many of those combos would continue? What weights do those combos have?
          </div>
        </li>
        <li>
          Which means that your opponent will be folding {{ foldEquity }}% of the time.
          <toggle-help-button
            v-bind:helpAreaVisible="helpAreaVisible.reasoning[2]"
            v-on:toggleHelp="toggleHelp('reasoning', 2)"
          />
          <div class="well help-section" v-if="helpAreaVisible.reasoning[2]">
            Continuing means calling or raising, so if you're not continuing, you're folding.
          </div>
        </li>
        <li>Which means that your bluff will work {{ foldEquity }}% of the time.</li>
        <li>{{ foldEquity }}% is <span v-if="isBluffImmediatelyProfitable">more</span><span v-else>less</span> than the {{ immediatelyProfitableBreakevenPercentage }}% you need in order to be <span v-if="streetToBluff !== 'river'">immediately </span>profitable.</li>
        <li>Therefore, your {{ bluffSize }} pot {{ streetToBluff }} bluff will <span v-if="!isBluffImmediatelyProfitable">not </span>be <span v-if="streetToBluff !== 'river'">immediately </span>profitable.</li>
      </ul>
      <ul v-else>
        <li>
          A {{ bluffSize }} pot bluff needs to work more than {{ immediatelyProfitableBreakevenPercentage }}% of the time in order to be <span v-if="streetToBluff !== 'river'">immediately </span>profitable.
          <toggle-help-button
            v-bind:helpAreaVisible="helpAreaVisible.reasoning[0]"
            v-on:toggleHelp="toggleHelp('reasoning', 0)"
          />
          <reasoning-help-section-1 v-if="helpAreaVisible.reasoning[0]"/>
        </li>
        <li>According to the ranges that you set, your bluff will get <span v-if="numPlayers === 2">both</span><span v-else>all</span> opponents to fold {{ foldEquity }}% of the time.</li>
        <li>{{ foldEquity }}% is <span v-if="isBluffImmediatelyProfitable">more</span><span v-else>less</span> than the {{ immediatelyProfitableBreakevenPercentage }}% you need in order to be <span v-if="streetToBluff !== 'river'">immediately </span>profitable.</li>
        <li>Therefore, your {{ bluffSize }} pot {{ streetToBluff }} bluff will <span v-if="!isBluffImmediatelyProfitable">not </span>be <span v-if="streetToBluff !== 'river'">immediately </span>profitable.</li>
      </ul>
      <hr v-if="numPlayers > 1">
      <cumulative-hit-percentages
        v-if="numPlayers > 1"
        v-bind:cumulativeHitPercentages="cumulativeHitPercentages"
        v-bind:moreThanTwoPlayers="numPlayers > 2"
        v-bind:streetsToDisplay="cumulativeHitPercentagesStreetsToDisplay"
      />
    </section>
  </section>
</template>

<script>
import CumulativeHitPercentages from '@/components/cumulative-hit-percentages';
import ToggleHelpButton from '@/components/guided-analyses/help/toggle-help-button';
import ReasoningHelpSectionOne from '@/components/guided-analyses/should-you-bluff/help/reasoning-1';

export default {
  name: 'should-you-bluff-initial-conclusion',
  components: {
    'cumulative-hit-percentages': CumulativeHitPercentages,
    'toggle-help-button': ToggleHelpButton,
    'reasoning-help-section-1': ReasoningHelpSectionOne,
  },
  props: [
    'bluffSize',
    'streetToBluff',
    'hitPercentageForStreetToBluff',
    'foldEquity',
    'immediatelyProfitableBreakevenPercentage',
    'cumulativeHitPercentages',
    'numPlayers',
  ],
  data: function () {
    return {
      profitableBreakevenPercentage: 30,
      helpAreaVisible: {
        reasoning: [false, false, false],
      },
    };
  },
  computed: {
    isBluffImmediatelyProfitable: function () {
      return this.foldEquity > this.immediatelyProfitableBreakevenPercentage;
    },
    cumulativeHitPercentagesStreetsToDisplay: function () {
      if (this.streetToBluff === 'flop') {
        return ['flop'];
      } else if (this.streetToBluff === 'turn') {
        return ['flop', 'turn'];
      } else if (this.streetToBluff === 'river') {
        return ['flop', 'turn', 'river'];
      }
    },
  },
  methods: {
    toggleHelp: function (type, index) {
      if (type === 'reasoning') {
        this.helpAreaVisible.reasoning[index] = !this.helpAreaVisible.reasoning[index];
      }

      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import 'src/assets/help-section';

  .should-you-bluff-initial-conclusion {
    font-size: 18px;

    pre {
      font-size: 16px;
    }

    li, p, table {
      max-width: 800px;
    }

    .well {
      margin-top: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/guided-analyses/should-you-bluff/conclusions/initial-conclusion.vue